import React, { useState, useEffect, useCallback } from 'react';
import { Search, Calendar, Flag, User } from 'lucide-react';
import FormControl from '../../../components/common/FormControl';
import Badge from '../../../components/common/Badge';
import Pagination from '../../../components/common/Pagination';
import Toast, { ToastMessage } from '../../../components/common/Toast';
import NoDataFound from '../../../components/common/NoDataFound';
import Skeleton from '../../../components/common/Skeleton';
import { statusVariant, severityBadgeCss } from '../../../utils/statusVariant';
import { tasksService, Task } from '../../../services/tasksService';
import { useEntitySettings } from '../../../contexts/EntitySettingsContext';
import { usePermission } from '../../../contexts/PermissionContext';

const PRIORITY_ICON: Record<string, string> = {
  CRITICAL: 'text-red-500',
  HIGH:     'text-orange-500',
  MEDIUM:   'text-yellow-500',
  LOW:      'text-green-500',
};

export const TasksTab: React.FC = () => {
  const { formatDate: fmtEntityDate } = useEntitySettings();
  const fmtDate = (d?: string | null) => (d ? fmtEntityDate(d) : '—');
  const { allowed: canView, loading: viewLoading } = usePermission('PRM_PROJECTS', 'view');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [priorityFilter, setPriorityFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const data = await tasksService.list({ limit: 300 });
      setTasks(data.filter(t => t.project_id != null));
    } catch (e: any) {
      setToast({ type: 'error', message: e?.message || 'Failed to load tasks' });
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [searchTerm, statusFilter, priorityFilter, pageSize]);

  const isOverdue = (date?: string | null) => !!date && new Date(date) < new Date();

  const filtered = tasks.filter(t => {
    const matchStatus = statusFilter === 'ALL' || t.status === statusFilter;
    const matchPriority = priorityFilter === 'ALL' || (t.priority || '') === priorityFilter;
    const s = searchTerm.toLowerCase();
    const matchSearch = !s ||
      (t.name || '').toLowerCase().includes(s) ||
      (t.project_name || '').toLowerCase().includes(s) ||
      (t.doer_name || '').toLowerCase().includes(s);
    return matchStatus && matchPriority && matchSearch;
  });

  const totalRecords = filtered.length;
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  if (!viewLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to Project Management tasks." />
      </div>
    );
  }

  return (
    <>
      <div className="netbody-card flex flex-col h-full">

        {/* Toolbar — Show LEFT · Status + Priority + search RIGHT */}
        <div className="netbody-header">
          <div className="flex items-center gap-2 ml-auto">
            <FormControl.SearchSelect
              value={statusFilter}
              onChange={v => setStatusFilter(String(v ?? 'ALL'))}
              className="filter-status"
              data-testid="SEL_pm_task_status_filter"
              noSearchIcon
              options={[
                { value: 'ALL', label: 'All Status' },
                { value: 'OPEN', label: 'Open' },
                { value: 'IN_PROGRESS', label: 'In Progress' },
                { value: 'PENDING', label: 'Pending' },
                { value: 'VERIFIED', label: 'Verified' },
                { value: 'DONE', label: 'Done' },
                { value: 'REJECTED', label: 'Rejected' },
                { value: 'CANCELLED', label: 'Cancelled' },
              ]}
            />

            <FormControl.Select
              value={priorityFilter}
              onChange={e => setPriorityFilter(e.target.value)}
              data-testid="SEL_pm_task_priority_filter"
            >
              <option value="ALL">All Priority</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </FormControl.Select>

            <FormControl.Input
              type="text"
              placeholder="Search tasks…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              leadingIcon={<Search size={14} />}
              className="filter-search"
              data-testid="INP_pm_task_search"
            />
          </div>
        </div>

        {/* Table */}
        <div className="netbody-content">
          <div className="scroll-container flex-1 overflow-hidden">
            <div className="table-list">
              <div className="table-list-scroll">
                <table className="table-list-table">
                  <thead>
                    <tr>
                      <th className="table-list-th table-list-cell--sno">S.No</th>
                      <th className="table-list-th">Task</th>
                      <th className="table-list-th">Project</th>
                      <th className="table-list-th">Assignee</th>
                      <th className="table-list-th">Due Date</th>
                      <th className="table-list-th">WBS</th>
                      <th className="table-list-th table-list-cell--priority">Priority</th>
                      <th className="table-list-th table-list-cell--status">Status</th>
                    </tr>
                  </thead>
                  <tbody aria-busy={loading} aria-live="polite">
                    {loading && <Skeleton.TableRows cols={['3xs', 'md', 'sm', 'sm', 'xs', 'sm', 'pill', 'pill']} />}
                    {!loading && filtered.length === 0 && (
                      <tr>
                        <td colSpan={8} className="table-list-cell">
                          <NoDataFound
                            title={searchTerm || statusFilter !== 'ALL' || priorityFilter !== 'ALL' ? 'No Matching Tasks' : 'No Project Tasks Found'}
                            message={searchTerm || statusFilter !== 'ALL' || priorityFilter !== 'ALL' ? 'No tasks match your search or filter.' : 'Tasks created under a project will appear here.'}
                          />
                        </td>
                      </tr>
                    )}
                    {!loading && paginated.map((t, idx) => (
                      <tr key={t.id} className="table-list-row" data-testid={`ROW_pm_task_${t.id}`}>
                        <td className="table-list-cell table-list-cell--sno">
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className="table-list-cell">
                          <span className="table-list-cell-title">{t.name}</span>
                        </td>
                        <td className="table-list-cell whitespace-nowrap">
                          {t.project_name || '—'}
                        </td>
                        <td className="table-list-cell">
                          <div className="flex items-center gap-1.5 whitespace-nowrap">
                            <User size={11} className="text-gray-400" />
                            {t.doer_name || '—'}
                          </div>
                        </td>
                        <td className="table-list-cell whitespace-nowrap">
                          <div className={`flex items-center gap-1 ${isOverdue(t.due_date) && t.status !== 'DONE' ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                            <Calendar size={11} />
                            {fmtDate(t.due_date)}
                          </div>
                        </td>
                        <td className="table-list-cell whitespace-nowrap">
                          {t.wbs_code
                            ? <span className="status-badge xs info">{t.wbs_code}</span>
                            : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="table-list-cell">
                          <div className="flex items-center gap-1">
                            {t.priority && <Flag size={11} className={PRIORITY_ICON[t.priority] || 'text-gray-400'} />}
                            {t.priority
                              ? <span className={severityBadgeCss(t.priority)}>{t.priority}</span>
                              : <span className="text-gray-400">—</span>}
                          </div>
                        </td>
                        <td className="table-list-cell">
                          <Badge variant={statusVariant(t.status)} size="sm">
                            {t.status.replace('_', ' ')}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={page}
                pageSize={pageSize}
                totalRecords={totalRecords}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                recordLabel="tasks"
              />
            </div>
          </div>
        </div>

      </div>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default TasksTab;
